import { useQueries } from 'react-query';
import { usePublicApiClient } from '../usePublicApiClient';
import { ContentfulThemeApiClient } from '@jbhi-fi/apis/src/contentfulThemeApi';
import { ContentfulTooltip } from '@jbhi-fi/apis/src/contentful/types';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { useCustomerContext } from '../../context/CustomerContextProvider';

export type UseContentfulTooltipsResult = {
  isLoading?: boolean;
  tooltips?: Array<ContentfulTooltip>;
};

export const useContentfulTooltips = ({
  contentfulThemeApiClient: contentfulThemeApiClientProp,
}: {
  contentfulThemeApiClient?: ContentfulThemeApiClient;
}): UseContentfulTooltipsResult => {
  const { contentfulThemeApiClient: contentfulThemeApiClientContext } = usePublicApiClient();
  const contentfulThemeApiClient = contentfulThemeApiClientProp || contentfulThemeApiClientContext;
  if (!contentfulThemeApiClient) {
    throw new Error(
      'useContentfulTooltips must be provided with a contentfulThemeApiClient via PublicApiClientProvider or props'
    );
  }

  const isMobile = useBreakpoint('md', 'down');
  let accountType: string = 'Guest';
  const { isLoggedIn = false, isPerks = false } = useCustomerContext();
  if (isLoggedIn && !isPerks) accountType = 'Account';
  if (isLoggedIn && isPerks) accountType = 'Perks';

  const showOnValues = isMobile ? ['Mobile only', 'Desktop & Mobile'] : ['Desktop only', 'Desktop & Mobile'];

  const queries = showOnValues.map(showOn => ({
    enabled: Boolean(contentfulThemeApiClient),
    queryKey: ['useContentfulTooltips', showOn, accountType],
    refetchOnWindowFocus: false,
    queryFn: () =>
      contentfulThemeApiClient?.getEntries<ContentfulTooltip>({
        content_type: 'tooltip',
        include: '1',
        cache: 'medium',
        'fields.showOn': showOn,
        'fields.showFor': accountType,
      }),
  }));

  const results = useQueries(queries);

  const isLoading = results.some(result => result.isLoading);
  const tooltipData = results.flatMap(result => result.data?.data?.items ?? []);

  return {
    isLoading,
    tooltips: tooltipData ?? [],
  };
};
